import { useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
// AI 모델 선택 - Gemini 2.0 Flash 또는 OpenAI
import { generateBusinessGuide as generateGeminiGuide } from '../lib/gemini';
import { generateBusinessGuide as generateOpenAIGuide } from '../lib/openai';

// Gemini를 기본으로 사용 (더 빠르고 토큰 제한이 넉넉함)
const USE_GEMINI = true;

export default function BusinessForm({ onGuideGenerated }) {
  const [formData, setFormData] = useState({
    industry: '',
    customIndustry: '',
    stage: '',
    target: '',
    budget: '',
    concerns: '',
    // 새로운 맞춤형 필드들
    location: '',          // 사업 지역
    experience: '',        // 경험/배경
    resources: '',         // 보유 자원/기술
    timeline: '',          // 목표 달성 기간
    specific_goal: '',     // 구체적 목표 (매출, 고객수 등)
    competition: '',       // 주요 경쟁사/경쟁 상황
    unique_advantage: '',  // 본인만의 강점/차별점
    current_situation: ''  // 현재 상황/문제점
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentModel, setCurrentModel] = useState(USE_GEMINI ? 'Gemini 2.0 Flash' : 'OpenAI GPT-3.5');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // 기타 선택시 직접 입력한 업종 사용
      const finalFormData = {
        ...formData,
        industry: formData.industry === '기타' ? formData.customIndustry : formData.industry
      };
      console.log('폼 데이터:', finalFormData);
      console.log('사용 중인 AI 모델:', USE_GEMINI ? 'Gemini 2.0 Flash' : 'OpenAI GPT-3.5');

      // Gemini 우선 시도, 실패시 OpenAI로 fallback
      let guide;
      if (USE_GEMINI) {
        try {
          setCurrentModel('Gemini 2.0 Flash');
          guide = await generateGeminiGuide(finalFormData);
        } catch (geminiError) {
          console.warn('Gemini 실패, OpenAI로 fallback:', geminiError.message);
          setCurrentModel('OpenAI GPT-3.5 (Fallback)');
          guide = await generateOpenAIGuide(finalFormData);
        }
      } else {
        setCurrentModel('OpenAI GPT-3.5');
        guide = await generateOpenAIGuide(finalFormData);
      }

      onGuideGenerated(guide);
    } catch (err) {
      console.error('에러 발생:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const industries = [
    '카페/음식점', 'IT/소프트웨어', '온라인쇼핑몰', '뷰티/헬스케어',
    '교육/강의', '부동산', '제조업', '서비스업', '기타'
  ];

  const stages = [
    '아이디어 단계', '사업 계획 수립', '창업 준비', '운영 중', '확장 계획'
  ];

  const budgets = [
    '500만원 이하', '500만원-1천만원', '1천만원-5천만원', '5천만원-1억원', '1억원 이상'
  ];

  return (
    <div className="card p-8 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-center">비즈니스 정보 입력</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 업종 선택 */}
        <div>
          <label className="block text-sm font-medium mb-2">업종 *</label>
          <select
            name="industry"
            value={formData.industry}
            onChange={handleChange}
            required
            className="input w-full"
          >
            <option value="">업종을 선택하세요</option>
            {industries.map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>

          {/* 기타 선택시 직접 입력 필드 */}
          {formData.industry === '기타' && (
            <div className="mt-3">
              <input
                type="text"
                name="customIndustry"
                value={formData.customIndustry}
                onChange={handleChange}
                placeholder="업종을 직접 입력해주세요 (예: 펜션운영, 유튜브채널, 드론촬영 등)"
                required
                className="input w-full"
              />
            </div>
          )}
        </div>

        {/* 사업 단계 */}
        <div>
          <label className="block text-sm font-medium mb-2">사업 단계 *</label>
          <select
            name="stage"
            value={formData.stage}
            onChange={handleChange}
            required
            className="input w-full"
          >
            <option value="">단계를 선택하세요</option>
            {stages.map(stage => (
              <option key={stage} value={stage}>{stage}</option>
            ))}
          </select>
        </div>

        {/* 타겟 고객 */}
        <div>
          <label className="block text-sm font-medium mb-2">주요 타겟 고객 *</label>
          <input
            type="text"
            name="target"
            value={formData.target}
            onChange={handleChange}
            placeholder="예: 20-30대 직장인, 소상공인, 대학생 등"
            required
            className="input w-full"
          />
        </div>

        {/* 예산 규모 */}
        <div>
          <label className="block text-sm font-medium mb-2">초기 투자 예산</label>
          <select
            name="budget"
            value={formData.budget}
            onChange={handleChange}
            className="input w-full"
          >
            <option value="">예산 규모를 선택하세요</option>
            {budgets.map(budget => (
              <option key={budget} value={budget}>{budget}</option>
            ))}
          </select>
        </div>

        {/* 사업 지역 */}
        <div>
          <label className="block text-sm font-medium mb-2">사업 지역/위치 *</label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="예: 강남구 신사동, 부산 해운대구, 온라인 전국, 수도권 중심 등"
            required
            className="input w-full"
          />
        </div>

        {/* 현재 상황 */}
        <div>
          <label className="block text-sm font-medium mb-2">현재 상황 및 배경 *</label>
          <textarea
            name="current_situation"
            value={formData.current_situation}
            onChange={handleChange}
            placeholder="예: 직장인이지만 부업으로 시작, 퇴사 후 창업 준비 중, 기존 사업 확장, 학생이지만 창업 도전 등"
            rows={2}
            required
            className="input w-full min-h-[60px] resize-y"
          />
        </div>

        {/* 보유 자원/기술 */}
        <div>
          <label className="block text-sm font-medium mb-2">보유 자원 및 강점 *</label>
          <textarea
            name="unique_advantage"
            value={formData.unique_advantage}
            onChange={handleChange}
            placeholder="예: 요리 15년 경력, 마케팅 전문 지식, 네트워크 보유, 개발 가능, 디자인 실력, 특정 분야 자격증 등"
            rows={2}
            required
            className="input w-full min-h-[60px] resize-y"
          />
        </div>

        {/* 구체적 목표 */}
        <div>
          <label className="block text-sm font-medium mb-2">구체적 목표 *</label>
          <input
            type="text"
            name="specific_goal"
            value={formData.specific_goal}
            onChange={handleChange}
            placeholder="예: 6개월 내 월매출 500만원, 1년 내 고객 1000명, 3개월 내 매장 오픈 등"
            required
            className="input w-full"
          />
        </div>

        {/* 목표 달성 기간 */}
        <div>
          <label className="block text-sm font-medium mb-2">목표 달성 희망 기간</label>
          <select
            name="timeline"
            value={formData.timeline}
            onChange={handleChange}
            className="input w-full"
          >
            <option value="">기간을 선택하세요</option>
            <option value="1개월">1개월 내</option>
            <option value="3개월">3개월 내</option>
            <option value="6개월">6개월 내</option>
            <option value="1년">1년 내</option>
            <option value="2년 이상">2년 이상</option>
          </select>
        </div>

        {/* 경쟁 상황 */}
        <div>
          <label className="block text-sm font-medium mb-2">경쟁사 및 시장 상황</label>
          <textarea
            name="competition"
            value={formData.competition}
            onChange={handleChange}
            placeholder="예: 주변에 카페 5개, 배달앱 업체들과 경쟁, 대형 프랜차이즈와 경쟁, 경쟁사 거의 없음 등"
            rows={2}
            className="input w-full min-h-[60px] resize-y"
          />
        </div>

        {/* 주요 관심사 */}
        <div>
          <label className="block text-sm font-medium mb-2">가장 큰 고민 및 우려사항</label>
          <textarea
            name="concerns"
            value={formData.concerns}
            onChange={handleChange}
            placeholder="예: 초기 고객 확보 방법, 자금 부족 문제, 마케팅 노하우 부족, 경쟁 대응 방법 등"
            rows={3}
            className="input w-full min-h-[80px] resize-y"
          />
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary w-full py-3 text-lg flex items-center justify-center"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin h-5 w-5 mr-2" />
              {currentModel}가 분석 중입니다... (약 10초)
            </>
          ) : (
            <>
              5분 만에 비즈니스 가이드 생성하기
              <ArrowRight className="ml-2 h-5 w-5" />
            </>
          )}
        </button>
      </form>

      <p className="text-center text-sm text-gray-500 mt-4">
        * 필수 항목은 반드시 입력해주세요
      </p>

      {/* AI 모델 표시 */}
      <div className="text-center mt-4 text-sm text-gray-500">
        현재 사용 중인 AI: <span className="font-semibold text-blue-600">{currentModel}</span>
        {currentModel.includes('Gemini') && <span className="text-green-600 ml-2">(더 빠르고 안정적)</span>}
        {currentModel.includes('Fallback') && <span className="text-orange-600 ml-2">(자동 대체됨)</span>}
      </div>
    </div>
  );
}